import React, { useState } from "react";
import "../styles/contact.css";
import SuccessModal from "../components/success_model";
import { postData } from "../api"; // <-- uses your API helper

function ContactForm() {
  const [showModal, setShowModal] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    contactNumber: "",
    subject: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [honeypot, setHoneypot] = useState(""); // basic spam trap

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isInvalid = (field) => submitted && !String(formData[field] || "").trim();

  const validate = () => {
    const { firstName, lastName, email, message } = formData;
    if (!firstName.trim()) return "First name is required.";
    if (!lastName.trim()) return "Last name is required.";
    if (!email.trim()) return "Email address is required.";
    // super-light email check (backend still validates)
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()))
      return "Please enter a valid email address.";
    if (!message.trim()) return "Message is required.";
    return null;
  };

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      contactNumber: "",
      subject: "",
      message: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSubmitted(true);

    // spam trap
    if (honeypot) {
      setErrorMsg("Submission blocked.");
      return;
    }

    const err = validate();
    if (err) {
      setErrorMsg(err);
      return;
    }

    setSubmitting(true);
    try {
      // Backend expects: firstName, lastName, email, phone?, subject?, message
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.contactNumber.trim() || undefined,
        subject: formData.subject.trim() || undefined,
        message: formData.message.trim(),
      };

      const res = await postData("contact", payload);

      // Accept either exact success shape or anything truthy with id
      const ok =
        (res && (res.status === "RECEIVED" || res.status === "received")) ||
        (res && res.id);

      if (ok) {
        resetForm();
        setSubmitted(false);
        setShowModal(true);
      } else {
        setErrorMsg("Could not submit right now. Please try again.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong. Please try again.";
      setErrorMsg(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container">
      <h5 className="text-uppercase text-muted">Get in touch with us</h5>
      <h2 className="mb-4 dark-text">SEND US A MESSAGE</h2>

      <form onSubmit={handleSubmit} noValidate>
        <div className="row mb-3">
          <div className="col-md-6">
            <label className="form-label">First Name *</label>
            <input
              type="text"
              name="firstName"
              className="form-control"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Enter first name"
            />
            {isInvalid("firstName") && (
              <div className="text-danger small">First name is required.</div>
            )}
          </div>
          <div className="col-md-6">
            <label className="form-label">Last Name *</label>
            <input
              type="text"
              name="lastName"
              className="form-control"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Enter last name"
            />
            {isInvalid("lastName") && (
              <div className="text-danger small">Last name is required.</div>
            )}
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Email *</label>
          <input
            type="email"
            name="email"
            className="form-control"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter email address"
          />
          {isInvalid("email") && (
            <div className="text-danger small">Email address is required.</div>
          )}
        </div>

        <div className="mb-3">
          <label className="form-label">Contact Number</label>
          <input
            type="text"
            name="contactNumber"
            className="form-control"
            value={formData.contactNumber}
            onChange={handleChange}
            placeholder="Enter contact number"
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Subject</label>
          <input
            type="text"
            name="subject"
            className="form-control"
            value={formData.subject}
            onChange={handleChange}
            placeholder="Subject of the message"
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Message *</label>
          <textarea
            name="message"
            className="form-control"
            rows="5"
            value={formData.message}
            onChange={handleChange}
            placeholder="Your message here"
          />
          {isInvalid("message") && (
            <div className="text-danger small">Message is required.</div>
          )}
        </div>

        {/* Honeypot field (hidden) */}
        <input
          type="text"
          name="website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          style={{ display: "none" }}
          tabIndex={-1}
          autoComplete="off"
        />

        {errorMsg && (
          <div className="alert alert-danger mt-2" role="alert">
            {errorMsg}
          </div>
        )}

        <button
          type="submit"
          className="primary-dark-btn w-100"
          disabled={submitting}
        >
          {submitting ? "Sending..." : "Send Message"}
        </button>
      </form>

      <SuccessModal show={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
}

export default ContactForm;
